/**
 * @package FlorenceEGI — EGI — Home Supreme Featured Bento renderer
 * @author Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version 2.0.0
 * @date 2026-04-22
 * @purpose Island 2 — bento asimmetrico 8 celle. Cell 1 = hero. Consuma
 *          /api/home-supreme/featured-egis. Thumb fallback via FEPlate SVG.
 *          Overlay con id mono + likes oro + caption (title italica, author,
 *          price). Posizioni grid via nth-child in home-supreme.css.
 */

import { el, clear, formatPrice, lazyImg, buildSrcset, pickVariant } from "./utils";
import type { ImageVariants } from "./utils";
import { buildPlate, pickTone } from "./plate";

type EgiDto = {
    id: number;
    title: string;
    thumb: string | null;
    image?: ImageVariants;
    price: number | null;
    likes_count: number;
    url: string;
    creator: { id: number; name: string; nick_name: string | null } | null;
    collection: { id: number; name: string | null } | null;
};

function bentoCell(egi: EgiDto, index: number): HTMLElement {
    const a = el("a", {
        className: `fe-bento__cell ${index === 0 ? "fe-bento__cell--hero" : "fe-bento__cell--small"}`,
        attrs: { href: egi.url, "aria-label": egi.title },
    });

    if (egi.image || egi.thumb) {
        const isHero = index === 0;
        // Hero cell 50vw desktop → large. Satelliti 25vw → tablet.
        const targetPx = isHero ? 1000 : 500;
        const bestSrc = pickVariant(egi.image, targetPx) ?? egi.thumb ?? "";
        const srcset = buildSrcset(egi.image);
        const img = lazyImg(bestSrc, egi.title, {
            width: isHero ? 1000 : 500, height: isHero ? 1250 : 625,
            sizes: isHero ? "(min-width:900px) 50vw, 100vw" : "(min-width:900px) 25vw, 50vw",
            ...(srcset ? { srcset } : {}),
        });
        img.className = "fe-bento__media";
        a.appendChild(img);
    } else {
        const plate = buildPlate(`egi-${egi.id}`, pickTone(`egi-${egi.id}`));
        plate.setAttribute("class", "fe-bento__media");
        a.appendChild(plate);
    }

    const idTag = el("span", {
        className: "fe-bento__id",
        text: "EGI-" + String(egi.id).padStart(4, "0"),
    });
    a.appendChild(idTag);

    const likes = el("span", {
        className: "fe-bento__likes",
        attrs: { "aria-label": `${egi.likes_count} likes` },
    });
    likes.appendChild(document.createTextNode("♥ " + String(egi.likes_count)));
    a.appendChild(likes);

    a.appendChild(el("span", { className: "fe-bento__overlay", attrs: { "aria-hidden": "true" } }));

    const cap = el("div", { className: "fe-bento__caption" });
    cap.appendChild(el("span", { className: "fe-bento__title", text: egi.title }));
    const meta = el("div", { className: "fe-bento__meta" });
    meta.appendChild(el("span", {
        className: "fe-bento__author",
        text: egi.creator ? (egi.creator.nick_name ?? egi.creator.name) : "",
    }));
    meta.appendChild(el("span", {
        className: "fe-bento__price",
        text: formatPrice(egi.price),
    }));
    cap.appendChild(meta);
    a.appendChild(cap);

    return a;
}

export function renderFeaturedBento(host: HTMLElement, data: Record<string, unknown>): void {
    clear(host);
    const items = Array.isArray((data as { items?: EgiDto[] }).items)
        ? (data as { items: EgiDto[] }).items.slice(0, 8)
        : [];
    if (!items.length) {
        host.appendChild(el("p", { className: "empty", text: "—" }));
        return;
    }
    const grid = el("div", { className: "fe-bento", attrs: { role: "list" } });
    items.forEach((egi, i) => {
        const cell = bentoCell(egi, i);
        cell.setAttribute("role", "listitem");
        grid.appendChild(cell);
    });
    host.appendChild(grid);

    const cta = el("div", { className: "fe-bento__cta" });
    const link = el("a", {
        attrs: { href: "/home/galleries", "aria-label": "Tutte le collezioni" },
    });
    link.appendChild(document.createTextNode(host.dataset.ctaLabel ?? "Tutte le collezioni"));
    const arrow = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    arrow.setAttribute("width", "14");
    arrow.setAttribute("height", "10");
    arrow.setAttribute("viewBox", "0 0 14 10");
    arrow.setAttribute("fill", "none");
    arrow.setAttribute("aria-hidden", "true");
    const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
    p.setAttribute("d", "M1 5h12M9 1l4 4-4 4");
    p.setAttribute("stroke", "currentColor");
    p.setAttribute("stroke-width", "1");
    p.setAttribute("stroke-linecap", "round");
    p.setAttribute("stroke-linejoin", "round");
    arrow.appendChild(p);
    link.appendChild(arrow);
    cta.appendChild(link);
    host.appendChild(cta);
}
